/**
 * SCOPE-CONFIG-1 Level 2 — per-deal negotiated scope.
 *
 * One deal, one delta. A CPA who agrees to add two procedures or drop one for
 * a particular client records it here with a reason, and the same reason is
 * what §9 of that deal's report discloses — which is why the reason field is
 * mandatory rather than a courtesy. The disclosure sentences shown at the
 * bottom are the server's own, not a local paraphrase: what the operator reads
 * before saving is verbatim what the client will read in the report.
 *
 * A removal at deal level beats an addition at global level. That ordering is
 * resolved in core/, never here.
 */
import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import {
  clearScopeDelta,
  fetchScopeDelta,
  saveScopeDelta,
  type ScopeDeltaResponse,
} from '@/api/scope'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { formatApiError } from '@/utils/httpErrors'

function parseNumbers(raw: string): number[] {
  return raw
    .split(/[\s,]+/)
    .map(s => s.trim())
    .filter(Boolean)
    .map(Number)
    .filter(n => Number.isInteger(n) && n > 0)
}

export function NegotiatedScopePanel({ dealId }: { dealId: string }) {
  const [data, setData] = useState<ScopeDeltaResponse | null>(null)
  const [added, setAdded] = useState('')
  const [removed, setRemoved] = useState('')
  const [reason, setReason] = useState('')
  const [editing, setEditing] = useState(false)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function load() {
    setLoading(true)
    setError(null)
    try {
      const result = await fetchScopeDelta(dealId)
      setData(result)
      setAdded((result.scope_delta?.added ?? []).join(', '))
      setRemoved((result.scope_delta?.removed ?? []).join(', '))
      setReason(result.reason ?? '')
    } catch (err) {
      setError(formatApiError(err))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealId])

  async function save() {
    if (reason.trim() === '') {
      toast.error('A reason is required — it is what the report discloses.')
      return
    }
    setBusy(true)
    setError(null)
    try {
      const result = await saveScopeDelta(dealId, {
        added: parseNumbers(added),
        removed: parseNumbers(removed),
        reason: reason.trim(),
      })
      setData(result)
      setEditing(false)
      toast.success('Negotiated scope saved and disclosed in the report.')
    } catch (err) {
      setError(formatApiError(err))
    } finally {
      setBusy(false)
    }
  }

  async function clear() {
    setBusy(true)
    setError(null)
    try {
      const result = await clearScopeDelta(dealId)
      setData(result)
      setAdded('')
      setRemoved('')
      setReason('')
      setEditing(false)
      toast.success('Negotiated scope removed — the deal is back to its standard tier.')
    } catch (err) {
      setError(formatApiError(err))
    } finally {
      setBusy(false)
    }
  }

  if (loading) {
    return <div className="text-sm text-muted-foreground">Loading negotiated scope…</div>
  }
  if (!data) {
    return (
      <div className="space-y-2">
        <p className="text-sm text-destructive">{error ?? 'Negotiated scope is unavailable.'}</p>
        <Button variant="outline" size="sm" onClick={() => void load()}>Try again</Button>
      </div>
    )
  }

  const hasDelta = data.added.length > 0 || data.removed.length > 0

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center justify-between gap-3">
          <span>Negotiated scope</span>
          <span className="text-sm font-normal text-muted-foreground tabular-nums">
            {data.in_scope_count} procedures in scope
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {error && <p className="text-sm text-destructive whitespace-pre-wrap">{error}</p>}

        {!editing && (
          <>
            {hasDelta ? (
              <div className="space-y-3 text-sm">
                {data.added.length > 0 && (
                  <div>
                    <div className="font-medium text-primary">Added for this deal</div>
                    <ul className="mt-1 space-y-0.5 text-muted-foreground">
                      {data.added.map(f => (
                        <li key={f.number}>#{f.number} {f.title} <span className="opacity-70">({f.section})</span></li>
                      ))}
                    </ul>
                  </div>
                )}
                {data.removed.length > 0 && (
                  <div>
                    <div className="font-medium">Removed for this deal</div>
                    <ul className="mt-1 space-y-0.5 text-muted-foreground">
                      {data.removed.map(f => (
                        <li key={f.number}>#{f.number} {f.title} <span className="opacity-70">({f.section})</span></li>
                      ))}
                    </ul>
                  </div>
                )}
                {data.reason && (
                  <div>
                    <div className="font-medium">Reason</div>
                    <p className="text-muted-foreground">{data.reason}</p>
                  </div>
                )}
                {data.disclosure.length > 0 && (
                  <div className="rounded-md bg-muted/50 p-3">
                    <div className="font-medium mb-1">Disclosed in §9 of the report</div>
                    {data.disclosure.map((line, i) => (
                      <p key={i} className="text-muted-foreground">{line}</p>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                This deal uses the standard {data.scope_tier} scope. Adjust it only when
                the engagement letter says something different.
              </p>
            )}
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
                {hasDelta ? 'Edit negotiated scope' : 'Negotiate scope for this deal'}
              </Button>
              {hasDelta && (
                <Button variant="ghost" size="sm" onClick={() => void clear()} disabled={busy}>
                  Reset to standard scope
                </Button>
              )}
            </div>
          </>
        )}

        {editing && (
          <div className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label htmlFor="delta-add">Add procedure numbers</Label>
                <Input
                  id="delta-add"
                  placeholder="e.g. 41, 62"
                  value={added}
                  onChange={e => setAdded(e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="delta-remove">Remove procedure numbers</Label>
                <Input
                  id="delta-remove"
                  placeholder="e.g. 12"
                  value={removed}
                  onChange={e => setRemoved(e.target.value)}
                />
              </div>
            </div>
            <div>
              <Label htmlFor="delta-reason">Reason (required — disclosed to the client)</Label>
              <Textarea
                id="delta-reason"
                rows={2}
                value={reason}
                onChange={e => setReason(e.target.value)}
                placeholder="Agreed in the engagement letter dated …"
              />
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={() => void save()} disabled={busy}>
                {busy ? 'Saving…' : 'Save negotiated scope'}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                disabled={busy}
                onClick={() => {
                  setEditing(false)
                  setAdded((data.scope_delta?.added ?? []).join(', '))
                  setRemoved((data.scope_delta?.removed ?? []).join(', '))
                  setReason(data.reason ?? '')
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

export default NegotiatedScopePanel
